/* eslint-disable max-len */
const TYPING_TIMER_IN_MILLISECONDS = 750;
const API_TIMER_IN_MILLISECONDS = 4000;

/**
The PasswordValidate class injects a password validation
widget that checks the entered password for the
following criteria:

- Longer than 10 characters
- Shorter than 128 characters
- Contains an uppercase letter
- Contains a lowercase letter
- Contains a number

Optional checks:

- Password confirmation
  - Checks that passwords in two fields match each other
  - Enabled with the `secondPasswordSelector` option
- Password can't match email
  - Checks that entered password does not match entered email
  - Enabled with the `emailSelector` option
- Password history
  - Asynchronously checks that password does not match any
    previous 3 passwords
  - Enabled with EITHER the `tokenForApiValidation` or
    `sessionForApiValidation` options

Also include `/app/stylesheets/passwordValidate.css`
*/
class PasswordValidate {
  /**
   * @param {object} options
   * @param {string} options.tokenForApiValidation user's password reset token (for use outside of app.bombbomb.com (optional)
   * @param {string} options.sessionForApiValidation user's current session (for use in app.bombbomb.com (optional)
   * @param {string} options.passwordSelector query selector for password input to validate (required)
   * @param {string} options.emailSelector query selector for email input (optional, see `Password can't match email` optional check)
   * @param {string} options.secondPasswordSelector query selector for password confirmation input (optional, see `Password confirmation` optional check)
   * @param {string} options.criteriaContainerSelector query selector for the element AFTER WHICH to inject the criteria list
   * @param {string} options.submitSelector '#submit', query selector for the submit button (optional, disables the button when password is invalid)
   * @param {string} options.environment either 'PROD', 'DEV', 'LOCAL', identifies the environment for Password history check (defaults to 'PROD')
   * @param {number} options.iconSize
   * @param {number|string} options.iconRightPosition
   * @param {number} options.iconTopPosition
   * @param {boolean} options.includeSuccessSVGOnOtherFormInputs
   */
  constructor(options) {
    const {
      tokenForApiValidation,
      sessionForApiValidation,
      passwordSelector,
      emailSelector,
      secondPasswordSelector,
      criteriaContainerSelector,
      submitSelector,
      environment,
      iconSize,
      iconRightPosition,
      iconTopPosition,
      includeSuccessSVGOnOtherFormInputs,
    } = options;

    this.tokenForApiValidation = tokenForApiValidation;
    this.sessionForApiValidation = sessionForApiValidation;
    this.emailInput = emailSelector ? document.querySelector(emailSelector) : null;
    this.passwordInput = document.querySelector(passwordSelector);
    this.secondPasswordInput = secondPasswordSelector
      ? document.querySelector(secondPasswordSelector)
      : null;
    this.validationCriteriaDiv = document.querySelector(criteriaContainerSelector);
    this.criteriaListId = 'bb-criteria-list';
    this.submitButton = submitSelector ? document.querySelector(submitSelector) : null;
    this.environment = environment;
    this.iconSize = iconSize;
    this.iconRightPosition = iconRightPosition;
    this.iconTopPosition = iconTopPosition;
    this.errors = {
      secondPassword: { text: 'Passwords must match', value: true },
      email: { text: 'Password must not match email', value: true },
      history: {
        text: 'Password must not match any of previous 3 saved passwords',
        value: false,
      },
      short: {
        text: 'Password must be at least 10 characters long',
        value: true,
      },
      long: {
        text: 'Password must be shorter than 128 characters',
        value: true,
      },
      uppercase: {
        text: 'Password must contain an uppercase letter',
        value: true,
      },
      lowercase: {
        text: 'Password must contain a lowercase letter',
        value: true,
      },
      number: { text: 'Password must contain a number', value: true },
      specialCharacters: {
        text: 'Password must not contain the special character "<"',
        value: false,
      },
    };
    this.errorOrder = ['short', 'uppercase', 'lowercase', 'number', 'specialCharacters'];
    this.typingTimer = null;
    this.apiTimer = null;
    this.shouldDoHistoryCheck = this.tokenForApiValidation || this.sessionForApiValidation;
    this.areSvgsPositioned = false;
    this.includeSuccessSVGOnOtherFormInputs =
      typeof includeSuccessSVGOnOtherFormInputs === 'undefined'
        ? false
        : Boolean(includeSuccessSVGOnOtherFormInputs);
  }

  initialize() {
    if (this.emailInput) {
      this.emailInput.addEventListener('input', this.handleInput);
      this.errorOrder.unshift('email');
    }
    this.passwordInput.addEventListener('input', this.handleInput);
    if (this.secondPasswordInput) {
      this.secondPasswordInput.addEventListener('input', this.handleInput);
      this.errorOrder.unshift('secondPassword');
    }
    if (this.shouldDoHistoryCheck) {
      this.errorOrder.unshift('history');
    }
    if (this.submitButton) {
      const parentForm = this.passwordInput.closest('form');
      if (parentForm) {
        parentForm.addEventListener('input', this.handleInput);
      }
      this.setSubmitButtonDisabled(true);
    }

    this.injectCriteriaListContainer();
    this.modifyPasswordContainers();
    this.injectSvgContainer();
  }

  /** @type {(event: InputEvent) => void} */
  handleInput = (event) => {
    if (!this.areSvgsPositioned) {
      this.createSvgs();
      this.repositionSvgContainers();
      this.areSvgsPositioned = true;
    }

    if (event.target instanceof HTMLInputElement) {
      if (event.target.value.trim().length === 0) {
        // Trim value if it contains only whitespace
        // This allows for names with spaces in them
        event.target.value = event.target.value.trim();
      }

      if (event.target.validity.valid) {
        event.target.classList.add('is-valid');
        event.target.classList.remove('is-invalid');
      } else {
        event.target.classList.remove('is-valid');
        event.target.classList.add('is-invalid');
      }
    }

    this.setSvg(null);
    this.setSubmitButtonDisabled(true);
    this.resetTypingTimer();
    this.validate();
  };

  validate() {
    this.resetErrors();
    if (this.emailInput) {
      this.checkMatchesEmail();
    } else {
      this.errors.email.value = false;
    }
    if (this.secondPasswordInput) {
      this.checkMatchesPassword2();
    } else {
      this.errors.secondPassword.value = false;
    }
    this.checkLength();
    this.checkRequiredCharacters();
    this.checkInvalidCharacters();
    this.renderValidationCriteria();

    if (!this.shouldDoHistoryCheck) {
      this.errors.history.value = false;
      this.handleSynchronousErrors();
    }
  }

  setSubmitButtonDisabled(disableSubmitButton) {
    if (!this.submitButton) {
      return;
    }

    if (disableSubmitButton) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('bb-submit--disabled');
      return;
    }

    this.submitButton.disabled = false;
    this.submitButton.classList.remove('bb-submit--disabled');
  }

  renderValidationCriteria() {
    const criteriaItems = this.mapErrorsToListItems();

    if (criteriaItems.length) {
      this.appendErrorItems(criteriaItems);
    } else {
      this.criteriaList.innerHTML = '';
      this.criteriaList.classList.add('bb-criteria-list--hidden');
    }

    this.setCriteriaListErrorClass(this.errors.history.value);
  }

  mapErrorsToListItems() {
    return this.errorOrder
      .map((key) => this.errors[key])
      .filter((error) => error.value)
      .map((error) => {
        const criteriaItem = document.createElement('li');
        criteriaItem.classList.add('bb-criteria-item');
        criteriaItem.textContent = error.text;
        return criteriaItem;
      });
  }

  injectCriteriaListContainer() {
    if (!this.criteriaList) {
      this.criteriaList = document.createElement('ul');
      this.criteriaList.id = this.criteriaListId;
      this.criteriaList.classList.add('bb-criteria-list', 'bb-criteria-list--hidden');
      insertAfter(this.criteriaList, this.validationCriteriaDiv);
    }
  }

  appendErrorItems(errorItems) {
    this.criteriaList.innerHTML = '';
    this.criteriaList.classList.remove('bb-criteria-list--hidden');
    errorItems.forEach((item) => {
      this.criteriaList.appendChild(item);
    });
  }

  setCriteriaListErrorClass(shouldHaveErrorClass) {
    if (shouldHaveErrorClass) {
      this.criteriaList.classList.add('bb-criteria-list--error');
    } else {
      this.criteriaList.classList.remove('bb-criteria-list--error');
    }
  }

  checkMatchesEmail() {
    if (this.emailInput.value !== this.passwordInput.value) {
      this.errors.email.value = false;
    }
  }

  checkMatchesPassword2() {
    if (this.passwordInput.value === this.secondPasswordInput.value) {
      this.errors.secondPassword.value = false;
    }
  }

  checkLength() {
    if (this.passwordInput.value.length > 9) {
      this.errors.short.value = false;
    }
    if (this.passwordInput.value.length < 129) {
      this.errors.long.value = false;
      if (this.errorOrder.includes('long')) {
        this.errorOrder.splice(this.errorOrder.length - 4, 1);
      }
    } else if (!this.errorOrder.includes('long')) {
      this.errorOrder.splice(this.errorOrder.length - 3, 0, 'long');
    }
  }

  checkRequiredCharacters() {
    if (/[a-z]/g.test(this.passwordInput.value)) {
      this.errors.lowercase.value = false;
    }
    if (/[A-Z]/g.test(this.passwordInput.value)) {
      this.errors.uppercase.value = false;
    }
    if (/[0-9]/g.test(this.passwordInput.value)) {
      this.errors.number.value = false;
    }
  }

  checkInvalidCharacters() {
    if (/[<]/g.test(this.passwordInput.value)) {
      this.errors.specialCharacters.value = true;
    } else {
      this.errors.specialCharacters.value = false;
    }
  }

  getEnvironmentApiUrl() {
    switch (this.environment) {
      case 'LOCAL':
      case 'DEV':
        return `https://${this.environment.toLowerCase()}.api.bombbomb.com`;
      default:
        return 'https://api.bombbomb.com';
    }
  }

  checkPasswordHistory() {
    this.setSvg(this.loadingSvg);
    window.clearTimeout(this.apiTimer);
    this.apiTimer = setTimeout(() => this.handleApiTimeout(), API_TIMER_IN_MILLISECONDS);
    fetch(`${this.getEnvironmentApiUrl()}/v2/users/profile/passwordvalidate`, {
      method: 'POST',
      body: JSON.stringify({
        password_reset_token: this.tokenForApiValidation,
        password: this.passwordInput.value,
      }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.sessionForApiValidation}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('problem validating password');
        }
        return response.json();
      })
      .then((responseJson) => {
        let passwordFailsHistoryCheck = false;
        window.clearTimeout(this.apiTimer);
        if (responseJson.message && Array.isArray(responseJson.message)) {
          passwordFailsHistoryCheck = responseJson.message.includes(this.errors.history.text);
        }
        this.errors.history.value = passwordFailsHistoryCheck;
        if (passwordFailsHistoryCheck && this.doErrorsExist()) {
          this.setSvg(this.errorSvg);
          this.setSubmitButtonDisabled(true);
        } else {
          this.handleSynchronousErrors();
        }
        this.renderValidationCriteria();
      })
      .catch((error) => {
        window.clearTimeout(this.apiTimer);
        this.handleSynchronousErrors();
        console.error(error);
      });
  }

  handleSynchronousErrors = () => {
    const thereAreErrors = this.doErrorsExist();
    const thereArePasswordErrors = this.doPasswordErrorsExist();
    this.setSubmitButtonDisabled(thereAreErrors);
    this.setSvg(thereArePasswordErrors ? null : this.successSvg);
  };

  /**
   * @returns {boolean}
   */
  doPasswordErrorsExist = () => {
    return Object.values(this.errors).some((error) => error.value);
  };

  /**
   * @returns {boolean}
   */
  doErrorsExist = () => {
    if (this.doPasswordErrorsExist() === true) {
      return true;
    }

    // also check other elements in the form to see if they are valid
    const parentForm = this.passwordInput.closest('form');
    if (!parentForm) {
      console.warn('could not validate inputs in form: no parent form found');
      return false;
    }

    const inputs = parentForm.querySelectorAll('input');
    const inputsArray = Array.from(inputs);
    const allValid = inputsArray.every((input) => input.validity.valid);
    return !allValid;
  };

  resetTypingTimer() {
    if (this.typingTimer !== null) {
      window.clearTimeout(this.typingTimer);
    }
    this.typingTimer = setTimeout(() => this.handleTypingComplete(), TYPING_TIMER_IN_MILLISECONDS);
  }

  handleTypingComplete() {
    this.typingTimer = null;
    if (this.shouldDoHistoryCheck) {
      this.checkPasswordHistory();
    }
  }

  handleApiTimeout() {
    this.errors.history.value = false;
    this.renderValidationCriteria();
    if (!this.doErrorsExist()) {
      this.setSubmitButtonDisabled(false);
    }
  }

  resetErrors() {
    // set all values besides `history` to true
    Object.values(this.errors).forEach((error) => {
      // eslint-disable-next-line no-param-reassign
      error.value = error.text !== this.errors.history.text;
    });
  }

  setSvg(svg) {
    this.svgContainer.innerHTML = svg || '';

    if (this.secondPasswordInput) {
      this.confirmSvgContainer.innerHTML = svg || '';
    }
  }

  injectSvgContainer() {
    this.svgContainer = document.createElement('span');
    this.svgContainer.classList.add('bb-svg-container');
    insertAfter(this.svgContainer, this.passwordInput);

    if (this.secondPasswordInput) {
      this.confirmSvgContainer = document.createElement('span');
      this.confirmSvgContainer.classList.add('bb-svg-container');
      insertAfter(this.confirmSvgContainer, this.secondPasswordInput);
    }
    this.repositionSvgContainers();
  }

  /**
   * @param {HTMLElement|null} container
   */
  repositionSvgContainer(container) {
    if (container) {
      container.style.right =
        typeof this.iconRightPosition === 'string'
          ? this.iconRightPosition
          : `${this.iconRightPosition}px`;
      container.style.top = `${this.iconTopPosition}px`;
    }
  }

  repositionSvgContainers() {
    this.repositionSvgContainer(this.svgContainer);
    this.repositionSvgContainer(this.confirmSvgContainer);
  }

  createSvgs() {
    const inputTopOffset = this.passwordInput.offsetTop;
    const inputHeight = this.passwordInput.getBoundingClientRect().height;
    const svgSize = this.iconSize || 0.5 * inputHeight;

    this.loadingSvg = getLoadingSvg(svgSize);
    this.errorSvg = getErrorSvg(svgSize);
    this.successSvg = getSuccessSvg(svgSize);

    const defaultTopPosition = inputTopOffset + (inputHeight - svgSize) / 2;
    const defaultRightPosition = (inputHeight - svgSize) / 2;
    this.iconRightPosition = this.iconRightPosition || defaultRightPosition;
    this.iconTopPosition = this.iconTopPosition || defaultTopPosition;
  }

  modifyPasswordContainers() {
    const passwordParentEl = this.passwordInput.parentNode;
    passwordParentEl.style.position = 'relative';

    if (this.secondPasswordInput) {
      const confirmPasswordParentEl = this.secondPasswordInput.parentNode;
      confirmPasswordParentEl.style.position = 'relative';
    }
  }
}

// Helper Functions

function getLoadingSvg(size) {
  return `<svg width="${size}px"  height="${size}px"  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" class="lds-facebook" style="background: none;">
    <title>Loading</title>
    <rect x="19" y="17.5" width="12" height="65" fill="#dee1e1">
      <animate attributeName="y" calcMode="spline" values="-2;17.5;17.5" keyTimes="0;0.5;1" dur="1.5" keySplines="0 0.5 0.5 1;0 0.5 0.5 1" begin="-0.30000000000000004s" repeatCount="indefinite"></animate>
      <animate attributeName="height" calcMode="spline" values="104;65;65" keyTimes="0;0.5;1" dur="1.5" keySplines="0 0.5 0.5 1;0 0.5 0.5 1" begin="-0.30000000000000004s" repeatCount="indefinite"></animate>
    </rect>
    <rect x="44" y="17.4239" width="12" height="65.1522" fill="#dee1e1">
      <animate attributeName="y" calcMode="spline" values="2.874999999999993;17.5;17.5" keyTimes="0;0.5;1" dur="1.5" keySplines="0 0.5 0.5 1;0 0.5 0.5 1" begin="-0.15000000000000002s" repeatCount="indefinite"></animate>
      <animate attributeName="height" calcMode="spline" values="94.25000000000001;65;65" keyTimes="0;0.5;1" dur="1.5" keySplines="0 0.5 0.5 1;0 0.5 0.5 1" begin="-0.15000000000000002s" repeatCount="indefinite"></animate>
    </rect>
    <rect x="69" y="16.9785" width="12" height="66.0431" fill="#dee1e1">
      <animate attributeName="y" calcMode="spline" values="7.75;17.5;17.5" keyTimes="0;0.5;1" dur="1.5" keySplines="0 0.5 0.5 1;0 0.5 0.5 1" begin="0s" repeatCount="indefinite"></animate>
      <animate attributeName="height" calcMode="spline" values="84.5;65;65" keyTimes="0;0.5;1" dur="1.5" keySplines="0 0.5 0.5 1;0 0.5 0.5 1" begin="0s" repeatCount="indefinite"></animate>
    </rect>
  </svg>`;
}

function getErrorSvg(size) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}px" height="${size}px" viewBox="0 0 42.26 42.26"><title>Error</title><path d="M21.13,0a21.09,21.09,0,0,1,4.12.41,21.15,21.15,0,0,1,13.45,9A21,21,0,0,1,40.65,13a21.35,21.35,0,0,1,1.2,4,20.91,20.91,0,0,1,0,8.24,21.09,21.09,0,0,1-16.6,16.6,20.91,20.91,0,0,1-8.24,0,21.35,21.35,0,0,1-4-1.2A21,21,0,0,1,9.39,38.7,21.16,21.16,0,0,1,1.61,13a21,21,0,0,1,2-3.65A21.28,21.28,0,0,1,9.39,3.56,21,21,0,0,1,13,1.61a21.35,21.35,0,0,1,4-1.2A21.09,21.09,0,0,1,21.13,0Z" fill="red"/><path d="M20,22.78l-1.42-8.1V11.37a.59.59,0,0,1,.56-.56h4.08a.59.59,0,0,1,.56.56v3.26l-1.42,8.15a.58.58,0,0,1-.56.47H20.55A.62.62,0,0,1,20,22.78Zm1.15,3.12a2.84,2.84,0,0,1,2.79,2.8,2.79,2.79,0,0,1-2.79,2.73,2.76,2.76,0,0,1-2.73-2.73A2.79,2.79,0,0,1,21.11,25.9Z" fill="#fff"/></svg>`;
}

function getSuccessSvg(size) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}px" height="${size}px" viewBox="0 0 22 22">
  <title>Success</title>
  <circle cx="11" cy="11" r="11" fill="#6bb220" />
  <path
    d="M15.85,6.68A1,1,0,0,0,15,6.09a1,1,0,0,0-.88.49l-4,6.19L8.87,11.29a1,1,0,0,0-.78-.49,1.08,1.08,0,0,0-1,.59.74.74,0,0,0,.1.88l2,2.75a1.19,1.19,0,0,0,.88.5,1.21,1.21,0,0,0,.89-.5l4.91-7.46A.75.75,0,0,0,15.85,6.68Z"
    fill="#ffffff"
  />
  </svg>`;
}

function insertAfter(newNode, referenceNode) {
  referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
}
